/**
 * PR 6a — Phase 3 — useProposalKpis: aggregate the 3 KPIs displayed in
 * ProposalKpiBlock (Phase 4).
 *
 * Aggregation: weighted mean over unit × qty.
 *   aboveRev3Pct = Σ((unit − rev3) × qty) / Σ(rev3 × qty)
 *   aboveMinPct  = Σ((unit − min) × qty)  / Σ(min × qty)
 *   realGrossMarginPct (admin only) = Σ((unit − cost) × qty) / Σ(unit × qty)
 *
 * Sign is preserved (no clamp at zero) — a comercial under rev3 must surface
 * as a negative chip in the KPI block.
 *
 * Memoization dep: [role, version, items.length]. The response object itself
 * is a brand-new instance every fetch (Axios returns fresh objects), so
 * referencing `response` would defeat memoization. Version is the durable
 * identity of a revision; items.length covers add/remove. This is acceptable
 * because items can never mutate in place within a single version — every
 * write creates a new revision (oms invariant).
 *
 * NOTE for shim callers: components that build a ProposalResponse locally
 * (e.g. PricePanel preview, where unit_price_cents mutates within a single
 * logical "revision") MUST use a synthetic monotonic `version` token that
 * changes whenever the inputs change. Hard-coding `version: 0` will freeze
 * the memo on the first render. See PricePanel.buildKpiShim for the canonical
 * pattern (`syntheticVersion = priceCents`).
 *
 * Roles:
 *   - admin → all 3 KPIs computed.
 *   - comercial → realGrossMarginPct is always undefined.
 *
 * Edge cases:
 *   - empty items → all undefined.
 *   - denominator === 0 → that KPI is undefined (avoid /0 NaN leaking to UI).
 */

import { useMemo } from "react";

import type {
  AdminItemDTO,
  ComercialItemDTO,
  ProposalResponse,
} from "../model/Proposal";

export interface ProposalKpis {
  aboveRev3Pct: number | undefined;
  aboveMinPct: number | undefined;
  realGrossMarginPct: number | undefined;
}

const EMPTY: ProposalKpis = {
  aboveRev3Pct: undefined,
  aboveMinPct: undefined,
  realGrossMarginPct: undefined,
};

function weightedRatio(num: number, den: number): number | undefined {
  if (den === 0) return undefined;
  return num / den;
}

function aggregateCommon(
  items: ReadonlyArray<AdminItemDTO | ComercialItemDTO>,
): Pick<ProposalKpis, "aboveRev3Pct" | "aboveMinPct"> {
  let rev3Num = 0;
  let rev3Den = 0;
  let minNum = 0;
  let minDen = 0;
  for (const it of items) {
    const qty = it.quantity;
    const unit = it.unit_price_cents;
    if (it.rev3_cents !== undefined) {
      rev3Num += (unit - it.rev3_cents) * qty;
      rev3Den += it.rev3_cents * qty;
    }
    minNum += (unit - it.min_price_cents) * qty;
    minDen += it.min_price_cents * qty;
  }
  return {
    aboveRev3Pct: weightedRatio(rev3Num, rev3Den),
    aboveMinPct: weightedRatio(minNum, minDen),
  };
}

function aggregateAdminMargin(
  items: ReadonlyArray<AdminItemDTO>,
): number | undefined {
  let num = 0;
  let den = 0;
  for (const it of items) {
    const qty = it.quantity;
    const unit = it.unit_price_cents;
    num += (unit - it.cost_cents) * qty;
    den += unit * qty;
  }
  return weightedRatio(num, den);
}

function aggregate(response: ProposalResponse): ProposalKpis {
  if (response.role === "admin") {
    const items = response.revision.items;
    if (items.length === 0) return EMPTY;
    const common = aggregateCommon(items);
    return {
      ...common,
      realGrossMarginPct: aggregateAdminMargin(items),
    };
  }
  // comercial branch
  const items = response.revision.items;
  if (items.length === 0) return EMPTY;
  const common = aggregateCommon(items);
  return {
    ...common,
    realGrossMarginPct: undefined,
  };
}

export function useProposalKpis(response: ProposalResponse): ProposalKpis {
  return useMemo(
    () => aggregate(response),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- response object is replaced every fetch; identity comes from (role, version, items.length) per oms revision invariant (items immutable within a single version)
    [response.role, response.revision.version, response.revision.items.length],
  );
}
